import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { fetchCapabilities, Capabilities } from '../services/CapabilitiesService';

/**
 * The single place the UI asks what this session may do.
 *
 * Deliberately shaped like `LicenseContext` — one fetcher, one in-flight guard,
 * refreshed by the same `license-changed` event bus — because the two answer
 * adjacent questions and a second pattern here would be one more thing to keep
 * in step.
 *
 * THE RULE THIS EXISTS TO ENFORCE: components ask `capabilities.canX`, never
 * `tier === 'SOMETHING'` or `isAuthenticated`. A tier name is a fact about
 * billing; a capability is an answer to the question the UI is actually asking.
 * Both fail-open bugs of 2026-09-01 came from confusing the two, and renaming a
 * tier silently granted access.
 */
interface CapabilitiesContextValue {
  capabilities: Capabilities | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const CapabilitiesContext = createContext<CapabilitiesContextValue | null>(null);

export const CapabilitiesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);
  const [loading, setLoading] = useState(true);

  // Never two requests on the wire at once, and never a lost refresh.
  //
  // These two flags are the whole mechanism:
  //   fetchInProgress  - a request is out right now
  //   refreshQueued    - somebody asked for a refresh while it was out
  const fetchInProgressRef = useRef(false);
  const refreshQueuedRef = useRef(false);

  /**
   * Fetch the current capabilities, de-duplicating concurrent callers.
   *
   * A call that arrives while a request is already out does NOT start a second
   * request — but it is not thrown away either. It sets a flag, and the request
   * in progress fetches again when it finishes.
   *
   * THE FLAG IS WHY, AND IT MATTERS. The obvious version of this guard just
   * returns early and drops the second call. That is correct when the answer
   * cannot have changed — and wrong here, because a second call arriving is
   * usually the signal that IT HAS. Signing in is exactly that case: the request
   * already on the wire carries only the guest cookie, and the one being dropped
   * would carry the jwt. Discard it and the UI keeps showing "Guest" while the
   * user's own name sits in the corner. That was the 2026-09-07 bug.
   */
  const refresh = useCallback(async () => {
    if (fetchInProgressRef.current) {
      refreshQueuedRef.current = true;   // remember it; do not drop it
      return;
    }

    fetchInProgressRef.current = true;
    try {
      do {
        // Cleared BEFORE the await, never after: a call arriving while we are
        // waiting must be able to set it again and win another loop. Clearing
        // it afterwards would erase exactly the signal we are here to catch.
        refreshQueuedRef.current = false;
        setCapabilities(await fetchCapabilities());
      } while (refreshQueuedRef.current);
    } finally {
      // Always runs, including on a failed fetch, so a network error cannot
      // wedge this permanently "in progress" and refuse every later refresh.
      fetchInProgressRef.current = false;
      setLoading(false);
    }
  }, []);

  // First contact. This is also what mints the guest cookie for a visitor who
  // has never been here, so it must run for everyone, not only the signed in.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Same bus LicenseContext listens on. Signing in, applying a key, clearing a
  // licence and consuming quota all change what the session may do, and all
  // already dispatch this.
  useEffect(() => {
    const handler = () => { refresh(); };
    window.addEventListener('license-changed', handler);
    return () => window.removeEventListener('license-changed', handler);
  }, [refresh]);

  return (
    <CapabilitiesContext.Provider value={{ capabilities, loading, refresh }}>
      {children}
    </CapabilitiesContext.Provider>
  );
};

export const useCapabilities = (): CapabilitiesContextValue => {
  const ctx = useContext(CapabilitiesContext);
  if (!ctx) {
    throw new Error('useCapabilities must be used within a CapabilitiesProvider');
  }
  return ctx;
};
